<script setup lang="ts">
import { ref } from 'vue';
import { type OutboundDeliveryTransactionResponse, useListOutboundDeliveryTransactionApi } from '../api/listOutboundDeliveryTransactionApi';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { Dialog, DataTable, Column, Button } from 'primevue';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { formatNumber } from 'shared/common/domain/helperFunctions';

defineExpose({
  show
});

const fetchApi = useListOutboundDeliveryTransactionApi();
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const productStore = useProductStore();
const storageAreaStore = useStorageAreaStore();

const visible = ref(false);
const loading = ref(false);
const transactions = ref<OutboundDeliveryTransactionResponse[]>([]);

async function show(id: string) {
  try {
    loading.value = true;

    const result = await fetchApi.execute(id);
    if (result.ok) {
      transactions.value = result.transactions;
    }

    visible.value = true;
  } finally {
    loading.value = false;
  }
}

function onHide() {
  visible.value = false;
  transactions.value = [];
}

function productName(productId: string) {
  const product = productStore.getById(productId);
  return product?.name ?? '';
}

function storageArea(storageAreaId: string | null) {
  if (storageAreaId) {
    const storageArea = storageAreaStore.getById(storageAreaId);
    return storageArea?.name ?? '';
  }

  return '';
}

function formatDate(timestamp: string) {
  return languageStore.formatDateTime(timestamp);
}

function formatUser(user: string) {
  const [name] = user.split("|");
  return name;
}
</script>

<template>
  <Dialog modal @hide="onHide" :header="translations.outboundDelivery.transactionListDialog.title"
    v-model:visible="visible">
    <DataTable :value="transactions">
      <template #empty>{{ translations.outboundDelivery.transactionListDialog.noFound }}</template>
      <template #loading>{{ translations.outboundDelivery.transactionListDialog.loading }}</template>
      <Column field="transactionTimeUtc" :header="translations.outboundDelivery.transactionListDialog.transactionTime">
        <template #body="columnData">
          {{ formatDate(columnData.data.transactionTimeUtc) }}
        </template>
      </Column>
      <Column field="productId" :header="translations.generic.product">
        <template #body="columnData">
          {{ productName(columnData.data.productId) }}
        </template>
      </Column>
      <Column field="storageAreaId" :header="translations.generic.storageArea">
        <template #body="columnData">
          {{ storageArea(columnData.data.storageAreaId) }}
        </template>
      </Column>
      <Column field="userId" :header="translations.generic.user">
        <template #body="columnData">
          {{ formatUser(columnData.data.userId) }}
        </template>
      </Column>
      <Column field="weight" :header="translations.generic.weight">
        <template #body="columnData">
          {{ formatNumber(columnData.data.weight) }}
        </template>
      </Column>
      <Column field="comment" :header="translations.generic.note">
      </Column>
    </DataTable>
    <template #footer>
      <Button :label="translations.generic.cancel" icon="pi pi-times" text @click="onHide"></Button>
    </template>
  </Dialog>
</template>
